import { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';

export interface PopupConfig {
  id: string;
  priority: number;
  condition: (state: PopupState) => boolean;
  component: React.ComponentType<any>;
  props?: Record<string, any>;
  storageKey: string;
  targetVersion?: number;
}

export interface PopupState {
  shelter?: {
    is_petszel_enabled?: boolean;
  };
  [key: string]: any;
}

interface PopupManagerContextValue {
  registerPopup: (popup: PopupConfig) => void;
  unregisterPopup: (id: string) => void;
  dismissPopup: (id: string) => void;
  currentPopup: PopupConfig | null;
  popupState: PopupState;
  updatePopupState: (updates: Partial<PopupState>) => void;
}

const PopupManagerContext = createContext<PopupManagerContextValue | undefined>(undefined);

export const PopupManagerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [popups, setPopups] = useState<PopupConfig[]>([]);
  const [currentPopup, setCurrentPopup] = useState<PopupConfig | null>(null);
  const [popupState, setPopupState] = useState<PopupState>({});

  const evaluatePopups = useCallback(() => {


    // [APPROACH #2] 1. Get the current progress version from storage
    // const savedVersion = parseInt(localStorage.getItem('first_time_popups') || '0', 10);

    // Check localStorage for dismissed popups
    const eligiblePopups = popups.filter((popup) => {
      const dismissed = localStorage.getItem(`popup:${popup.storageKey}:dismissed`);
      if (dismissed) return false;


      // [APPROACH #2] 2. Logic Change: If the user's version is already >= this popup's target, 
      // // it means they've already seen this (or something further ahead).
      // if (savedVersion >= (popup.targetVersion || 0)) return false;


      // Check if condition is met
      return popup.condition(popupState);
    });

    // Sort by priority (higher number = higher priority)
    const sortedPopups = [...eligiblePopups].sort((a: PopupConfig, b: PopupConfig) => b.priority - a.priority);

    setCurrentPopup(sortedPopups[0] || null);
  }, [popups, popupState]);

  const registerPopup = useCallback((popup: PopupConfig) => {
    setPopups((prev) => {
      const exists = prev.find((p) => p.id === popup.id);
      if (exists) return prev;
      return [...prev, popup];
    });
  }, []);

  const unregisterPopup = useCallback((id: string) => {
    setPopups((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const dismissPopup = useCallback((id: string) => {
    // Mark as dismissed in localStorage
    const popup = popups.find((p) => p.id === id);
    if (popup) {
      localStorage.setItem(`popup:${popup.storageKey}:dismissed`, Date.now().toString());
      
      
      
      // [APPROACH #2] Save the version reached instead of a generic "dismissed" flag
      //localStorage.setItem('first_time_popups', popup.targetVersion.toString());
    }
    setCurrentPopup(null);
    // Trigger re-evaluation to show next popup in queue
    setTimeout(() => evaluatePopups(), 0);
  }, [popups, evaluatePopups]);

  const updatePopupState = useCallback((updates: Partial<PopupState>) => {
    setPopupState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Re-evaluate popups when state or popups change
  useEffect(() => {
    evaluatePopups();
  }, [evaluatePopups]);

  const contextValue = useMemo(
    () => ({
      registerPopup,
      unregisterPopup,
      dismissPopup,
      currentPopup,
      popupState,
      updatePopupState,
    }),
    [registerPopup, unregisterPopup, dismissPopup, currentPopup, popupState, updatePopupState]
  );

  return (
    <PopupManagerContext.Provider value={contextValue}>
      {children}
    </PopupManagerContext.Provider>
  );
};

export const usePopupManager = () => {
  const context = useContext(PopupManagerContext);
  if (!context) {
    throw new Error('usePopupManager must be used within PopupManagerProvider');
  }
  return context;
};
